import { Injectable, NgZone } from '@angular/core';
import { Router } from '@angular/router';
import { BehaviorSubject } from 'rxjs';

export interface TourStep {
  elementId: string;
  title: string;
  description: string;
  route?: string;
}

export interface TourState {
  active: boolean;
  step: number;
  total: number;
  current: TourStep | null;
}

@Injectable({ providedIn: 'root' })
export class TourService {
  private steps: TourStep[] = [];
  private _state = new BehaviorSubject<TourState>({ active: false, step: 0, total: 0, current: null });
  state$ = this._state.asObservable();

  constructor(private router: Router, private zone: NgZone) {}

  start(steps: TourStep[]): void {
    this.steps = steps;
    this.goToStep(0);
  }

  next(): void {
    const { step, total } = this._state.getValue();
    if (step < total - 1) this.goToStep(step + 1);
    else this.end();
  }

  prev(): void {
    const { step } = this._state.getValue();
    if (step > 0) this.goToStep(step - 1);
  }

  end(): void {
    this._state.next({ active: false, step: 0, total: 0, current: null });
  }

  private goToStep(index: number): void {
    const step = this.steps[index];
    const nav = step.route ? this.router.navigate([step.route]) : Promise.resolve(true);
    nav.then(() => {
      // run inside zone so BehaviorSubject subscribers trigger change detection
      this.zone.run(() => {
        this._state.next({ active: true, step: index, total: this.steps.length, current: step });
      });
    });
  }

  waitForElement(id: string, timeout = 4000): Promise<HTMLElement | null> {
    return new Promise(resolve => {
      const el = document.getElementById(id);
      if (el) { resolve(el); return; }
      const t0 = Date.now();
      const poll = setInterval(() => {
        const found = document.getElementById(id);
        if (found) { clearInterval(poll); resolve(found); return; }
        if (Date.now() - t0 > timeout) { clearInterval(poll); resolve(null); }
      }, 80);
    });
  }
}
